/* =========================================================
   ATS — Cart Drawer (Refined)
   - Single instance
   - Smooth scroll lock / restore
   - Works with ATS Note Sheet
   ========================================================= */

class ATSCartDrawer {
  constructor() {
    // Elements
    this.drawer    = document.getElementById('ATS-CartDrawer');
    this.panel     = this.drawer?.querySelector('.ATS-cart-panel');
    this.overlay   = this.drawer?.querySelector('.ATS-cart-overlay');
    this.closeBtn  = document.getElementById('ATS-CartDrawerClose');
    this.itemsEl   = document.getElementById('ATS-CartDrawerItems');
    this.totalEl   = document.getElementById('ATS-CartDrawerTotal');
    this.countEl   = document.getElementById('ATS-CartCount');
    this.contentEl = this.drawer?.querySelector('.ATS-cart-content');
    this._upsellSplide = null;

    // Flags / settings
    this.enabled  = this.drawer?.dataset.enabled === 'true';
    this.moneyFmt = this.drawer?.dataset.moneyFormat
      ? JSON.parse(this.drawer.dataset.moneyFormat)
      : 'Rs.{{amount}}';
    this.sideRight = this.panel?.classList.contains('tw-right-0');

    // State
    this.isOpen  = false;
    this._scrollY = 0;

    if (this.enabled && this.drawer && this.panel) {
      this.bind();
    }
  }

  bind() {
    /* Intercept ANY /cart/add form submit */
    document.addEventListener(
      'submit',
      (e) => {
        const form = e.target;
        if (!(form instanceof HTMLFormElement)) return;

        const action = (form.getAttribute('action') || '').replace(location.origin, '');
        if (!/^\/cart\/add/.test(action)) return;

        e.preventDefault();
        this.addForm(form);
      },
      true
    );

    /* Optional explicit button helper */
    document.addEventListener('click', (e) => {
      const btn = e.target.closest('[data-ats-add-to-cart]');
      if (!btn) return;

      const form = btn.closest('form');
      if (!form) return;

      e.preventDefault();
      this.addForm(form);
    });

    /* Overlay / close */
    this.overlay?.addEventListener('click', () => this.close());
    this.closeBtn?.addEventListener('click', () => this.close());

    /* Programmatic open event */
    document.addEventListener('ats:open-cart', () => {
      this.open();
      this.refresh();
    });

    /* Header cart button trigger */
    document.addEventListener('click', (e) => {
      const headerCart = e.target.closest('#data-ats-maindrw');
      if (!headerCart) return;

      // IMPORTANT: block default link behaviour (no scroll / navigation)
      e.preventDefault();

      this.open();
      this.refresh();
    });
  }

  /* ---------- UI control ---------- */

  open() {
    if (this.isOpen || !this.drawer || !this.panel) return;
    this.isOpen = true;

    // Lock scroll at current position
    this._scrollY = window.scrollY || document.documentElement.scrollTop || 0;
    document.body.classList.add('ATS-lock');
    document.body.style.top = `-${this._scrollY}px`;

    // Show drawer
    this.drawer.classList.remove('tw-hidden');
    this.drawer.removeAttribute('aria-hidden');

    // Hide content for fade-in
    this.contentEl?.classList.add('ATS-hidden');

    // Start collapsed
    if (this.overlay) this.overlay.style.opacity = '0';
    this.panel.style.transform = 'scaleX(0.01)';

    // Animate in
    requestAnimationFrame(() => {
      if (this.overlay) this.overlay.style.opacity = '1';
      this.panel.style.transform = 'scaleX(1)';

      setTimeout(() => {
        this.contentEl?.classList.remove('ATS-hidden');
      }, 300);
    });
  }

  close() {
    if (!this.isOpen || !this.drawer || !this.panel) return;
    this.isOpen = false;

    // Fade out content
    this.contentEl?.classList.add('ATS-hidden');

    // Shrink + fade overlay
    setTimeout(() => {
      this.panel.style.transform = 'scaleX(0.01)';
      if (this.overlay) this.overlay.style.opacity = '0';
    }, 200);

    // After animation: fully hide + restore scroll
    setTimeout(() => {
      this.drawer.classList.add('tw-hidden');
      this.drawer.setAttribute('aria-hidden', 'true');

      document.body.classList.remove('ATS-lock');
      document.body.style.top = '';

      // Restore original scroll position
      window.scrollTo(0, this._scrollY || 0);

      // Reset transforms
      this.panel.style.transform = '';
      if (this.overlay) this.overlay.style.opacity = '';
    }, 700);
  }

  /* ---------- Cart ops ---------- */

  async addForm(form) {
    const fd = new FormData(form);

    // Ensure variant ID
    if (!fd.get('id')) {
      const idInput = form.querySelector('input[name="id"], select[name="id"]');
      if (idInput?.value) fd.set('id', idInput.value);
    }

    // Open instantly
    this.open();

    // First time placeholder
    if (this.itemsEl && !this.itemsEl.innerHTML.trim()) {
      this.itemsEl.innerHTML = '<p>Loading…</p>';
    }

    try {
      await fetch('/cart/add.js', {
        method: 'POST',
        body: fd,
        headers: { 'X-Requested-With': 'XMLHttpRequest' },
      });
      this.refresh();
    } catch (e) {
      console.error('Add to cart failed', e);
    }
  }

  async refresh() {
    try {
      const res = await fetch('/cart.js', {
        cache: 'no-store',
        headers: { Accept: 'application/json' },
      });
      const cart = await res.json();

      const root   = this.drawer || document;
      const noEmpty = root.querySelector('.ATS-cart-noempty');
      const empty   = root.querySelector('.ATS-cart-empty');

      // Count & total
      if (this.countEl) this.countEl.textContent = String(cart.item_count || 0);
      if (this.totalEl) this.totalEl.innerHTML = `<p>${this.formatMoney(cart.total_price)}</p>`;

      // Items
      if (cart.item_count > 0) {
        noEmpty?.removeAttribute('hidden');
        empty?.setAttribute('hidden', '');
        if (this.itemsEl) {
          this.itemsEl.innerHTML = cart.items.map((i) => this.renderItem(i)).join('');
          this.attachItemHandlers(cart);
        }
      } else {
        empty?.removeAttribute('hidden');
        noEmpty?.setAttribute('hidden', '');
        if (this.itemsEl) this.itemsEl.innerHTML = '';
      }

      await this.renderUpsell(cart);
    } catch (e) {
      console.error('Cart refresh failed', e);
    }
  }

  attachItemHandlers(cart) {
    if (!this.itemsEl) return;

    // Remove
    this.itemsEl.querySelectorAll('[data-ats-remove]').forEach((a) => {
      a.addEventListener('click', async (e) => {
        e.preventDefault();
        const key = e.currentTarget.getAttribute('data-ats-remove');
        if (!key) return;

        await fetch('/cart/change.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ id: key, quantity: 0 }),
        });
        this.refresh();
      });
    });

    // Qty +/- 
    this.itemsEl.querySelectorAll('[data-ats-qty]').forEach((btn) => {
      btn.addEventListener('click', async (e) => {
        const key = e.currentTarget.getAttribute('data-ats-qty');
        const delta = parseInt(e.currentTarget.getAttribute('data-delta'), 10);
        if (!key || isNaN(delta)) return;

        const item = cart.items.find((i) => i.key === key);
        if (!item) return;

        const nextQty = Math.max(0, (item.quantity || 1) + delta);

        await fetch('/cart/change.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ id: key, quantity: nextQty }),
        });
        this.refresh();
      });
    });
  }

  async renderUpsell(cart) {
    const wrap = document.getElementById('ATS-CartUpsell');
    if (!wrap) return;

    const seed = cart.items?.[cart.items.length - 1] || cart.items?.[0];
    if (!seed) {
      wrap.innerHTML = '';
      return;
    }

    try {
      const url = `/recommendations/products.json?product_id=${seed.product_id}&limit=6`;
      const res = await fetch(url, { headers: { 'Cache-Control': 'no-cache' } });
      if (!res.ok) {
        wrap.innerHTML = '';
        return;
      }

      const data = await res.json();
      const products = data?.products || [];
      if (!products.length) {
        wrap.innerHTML = '';
        return;
      }

      wrap.innerHTML = `
        <h3>You may also like</h3>
        <div class="splide tw-m-1" data-ats-splide='{"perPage":3,"type":"loop","arrows":false,"pagination":false}'>
          <div class="splide__track">
            <ul class="splide__list ATS-gap">
              ${products
                .map((p) => {
                  const hasSingleVariant = Array.isArray(p.variants) && p.variants.length === 1;
                  const variantId = hasSingleVariant ? p.variants[0]?.id : null;

                  return `
                    <li class="splide__slide tw-relative tw-overflow-visible tw-border tw-rounded-[var(--media-radius)] tw-p-4 tw-mt-6 tw-mb-6 hover:tw-shadow-sm">
                      <button
                        class="ATS-upsell-plus tw-absolute tw-bottom-0 tw-right-4 tw-translate-x-1/2 -tw-translate-y-1/2
                               tw-z-10 tw-flex tw-items-center tw-justify-center tw-h-9 tw-w-9 tw-rounded-full
                               tw-bg-[rgb(var(--color-background))] tw-text-[rgb(var(--color-foreground))]
                               tw-border tw-border-[rgba(var(--color-foreground),0.10)]
                               tw-shadow-[0_2px_10px_rgba(0,0,0,0.08)]
                               hover:tw-bg-[rgb(var(--color-foreground))] hover:tw-text-[rgb(var(--color-background))]
                               hover:tw-border-[rgba(var(--color-foreground),0.20)]
                               focus:tw-ring-2 focus:tw-ring-[rgba(var(--color-foreground),0.15)] focus:tw-outline-none
                               tw-transition tw-duration-200"
                        data-upsell-add
                        ${variantId ? `data-variant-id="${variantId}"` : `data-url="${p.url}"`}
                        aria-label="${variantId ? 'Add to cart' : 'Choose options'}"
                        title="${variantId ? 'Add to cart' : 'Choose options'}"
                        type="button">
                        <svg width="18" height="18" viewBox="0 0 18 18" fill="none" aria-hidden="true">
                          <path d="M9 3.25v11.5M3.25 9h11.5" stroke="currentColor" stroke-width="1.75" stroke-linecap="round"/>
                        </svg>
                      </button>

                      <a href="${p.url}" class="tw-flex ATS-gap tw-items-start tw-no-underline tw-text-inherit">
                        ${
                          p.featured_image
                            ? `
                              <span class="tw-shrink-0 tw-block">
                                <img
                                  src="${p.featured_image}"
                                  alt="${(p.title || '').replace(/"/g, '&quot;')}"
                                  class="tw-w-20 tw-h-24 tw-object-cover tw-rounded-[calc(var(--media-radius)/2)]
                                         tw-border tw-border-[rgba(var(--color-foreground),0.10)]"
                                  loading="lazy"
                                  decoding="async"
                                >
                              </span>`
                            : ''
                        }
                        <div class="tw-min-w-0">
                          <div class="tw-text-lg tw-font-medium tw-line-clamp-2">${p.title || ''}</div>
                          <div class="tw-text-lg tw-mt-1 tw-opacity-80">
                            ${typeof p.price === 'number' ? this.formatMoney(p.price) : ''}
                          </div>
                        </div>
                      </a>
                    </li>
                  `;
                })
                .join('')}
            </ul>
          </div>
        </div>
      `;

      if (window.ATS_initSplide) window.ATS_initSplide(wrap);

      wrap.addEventListener('click', async (e) => {
        const btn = e.target.closest('[data-upsell-add]');
        if (!btn) return;
        e.preventDefault();

        const variantId = btn.getAttribute('data-variant-id');
        const productUrl = btn.getAttribute('data-url');

        if (variantId) {
          try {
            btn.classList.add('tw-opacity-60');
            await fetch('/cart/add.js', {
              method: 'POST',
              headers: { 'Content-Type': 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
              body: JSON.stringify({ id: Number(variantId), quantity: 1 }),
            });
            this.refresh();
          } catch (err) {
            console.error('Upsell add failed:', err);
          } finally {
            btn.classList.remove('tw-opacity-60');
          }
        } else if (productUrl) {
          window.location.href = productUrl;
        }
      });
    } catch (e) {
      wrap.innerHTML = '';
    }
  }

  renderItem(item) {
    const price = this.formatMoney(item.line_price);
    return `
      <div class="tw-grid tw-grid-cols-[72px_1fr_auto] ATS-gap tw-items-start">
        <img
          src="${item.image}"
          alt="${item.title}"
          class="tw-w-[86px] tw-h-[86px] tw-object-cover tw-rounded-[calc(var(--media-radius)/2)]"
        >
        <div class="tw-min-w-0">
          <p class="ATS-item-title h5">${item.product_title}</p>
          ${item.variant_title ? `<p class="ATS-variant">${item.variant_title}</p>` : ''}
          <ul class="tw-flex">
            <li class="tw-border tw-border-[rgba(var(--color-foreground),0.10)] tw-px-4 tw-py-2 tw-rounded-[var(--buttons-radius)] tw-overflow-hidden">
              <button class="ATS-qty-btn" data-ats-qty="${item.key}" data-delta="-1" aria-label="Decrease quantity">−</button>
              <span>${item.quantity}</span>
              <button class="ATS-qty-btn" data-ats-qty="${item.key}" data-delta="1" aria-label="Increase quantity">+</button>
            </li>
            <li class="tw-ml-4">
              <a class="ATS-remove caption" href="#" data-ats-remove="${item.key}">Remove</a>
            </li>
          </ul>
        </div>
        <div class="tw-text-right">
          <span class="price">${price}</span>
        </div>
      </div>
    `;
  }

  formatMoney(cents) {
    const value = (cents / 100).toFixed(2);
    return this.moneyFmt
      .replace(/{{\s*amount\s*}}/g, value)
      .replace(/{{\s*amount_no_decimals\s*}}/g, Math.round(cents / 100))
      .replace(/{{\s*amount_with_comma_separator\s*}}/g, value.replace('.', ','))
      .replace(
        /{{\s*amount_no_decimals_with_comma_separator\s*}}/g,
        Math.round(cents / 100).toString().replace('.', ',')
      );
  }
}

/* -------------------------
   Singleton + Note Sheet
   ------------------------- */
window.ATS = window.ATS || {};

document.addEventListener('DOMContentLoaded', () => {
  if (!window.ATS.cart) {
    window.ATS.cart = new ATSCartDrawer();
  }
  const cart    = window.ATS.cart;
  const sheet   = document.getElementById('ATS-NoteSheet');
  const openBtn = document.getElementById('ATS-AddNoteBtn');
  const closeBtn= document.getElementById('ATS-NoteClose');
  const saveBtn = document.getElementById('ATS-NoteSave');
  const input   = document.getElementById('ATS-NoteInput');

  if (!sheet) return;

  const openSheet = () => {
    sheet.classList.remove('tw-hidden');
    sheet.setAttribute('aria-hidden', 'false');
    requestAnimationFrame(() => sheet.querySelector('div')?.classList.remove('tw-translate-y-full'));
    input?.focus();
  };

  const closeSheet = () => {
    sheet.setAttribute('aria-hidden', 'true');
    sheet.querySelector('div')?.classList.add('tw-translate-y-full');
    setTimeout(() => sheet.classList.add('tw-hidden'), 300);
  };

  const saveNote = async () => {
    const note = (input?.value || '').trim();
    await fetch('/cart/update.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ note }),
    });
    closeSheet();
    cart.refresh();
  };

  openBtn?.addEventListener('click', openSheet);
  closeBtn?.addEventListener('click', closeSheet);
  saveBtn?.addEventListener('click', saveNote);
  document.addEventListener('keydown', (e) => e.key === 'Escape' && closeSheet());
});
